import React from 'react';
import { Document, Page, Text, View, Image, pdf, StyleSheet } from '@react-pdf/renderer';

/* -------------------------------------------------------------------------- */
/*                                  SETTINGS                                  */
/* -------------------------------------------------------------------------- */

const DEBUG = false;
const log = (...args) => DEBUG && console.log('[react-pdf-utils]', ...args);

// Environment checks
const isBrowserEnvironment = () =>
  typeof window !== 'undefined' &&
  typeof document !== 'undefined' &&
  typeof document.createElement === 'function';

const validateBrowserEnvironment = () => {
  if (!isBrowserEnvironment()) {
    throw new Error(
      'react-pdf-utils requires browser DOM APIs. This cannot run in Node.js. ' +
      'For server-side PDF generation, use Puppeteer or direct React-PDF components.'
    );
  }
};

/* -------------------------------------------------------------------------- */
/*                                    CONSTS                                  */
/* -------------------------------------------------------------------------- */

// Font and color constants
const DEFAULT_FONT_SIZE = 11;
const DEFAULT_FONT_FAMILY = 'Helvetica';
const DEFAULT_COLOR = '#2c3e50';
const PRIMARY_COLOR = '#3498db';
const SECONDARY_COLOR = '#7f8c8d';
const BORDER_COLOR = '#ecf0f1';
const TABLE_HEADER_BG = '#3498db';

// Checkbox symbols
const CHECKED_CHECKBOX = '☒';
const UNCHECKED_CHECKBOX = '☐';

// CSS selectors
const UI_ELEMENTS_SELECTOR =
  'button, .MuiButton-root, [style*="position: fixed"], .no-print, .ui-element';
const SCRIPT_STYLE_SELECTOR = 'script, style';
const CHECKBOX_SPANS_SELECTOR = '.pdf-checkbox';

/* -------------------------------------------------------------------------- */
/*                                    STYLES                                  */
/* -------------------------------------------------------------------------- */

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#fff',
    padding: 25, // 25mm margins on all sides
    fontSize: DEFAULT_FONT_SIZE,
    fontFamily: DEFAULT_FONT_FAMILY,
    color: DEFAULT_COLOR,
  },
  header: {
    textAlign: 'center',
    marginBottom: 40,
    borderBottom: `2 solid ${PRIMARY_COLOR}`,
    paddingBottom: 20,
  },
  logo: { width: 120, height: 60, marginBottom: 16, alignSelf: 'center' },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    textTransform: 'uppercase',
    marginBottom: 8,
    color: DEFAULT_COLOR,
    letterSpacing: 1,
  },
  subtitle: {
    fontSize: 14,
    color: SECONDARY_COLOR,
    marginBottom: 16,
    fontStyle: 'italic',
  },
  content: {
    fontSize: DEFAULT_FONT_SIZE,
    lineHeight: 1.6,
    color: DEFAULT_COLOR,
  },
  subheading: {
    fontSize: 14,
    fontWeight: 'bold',
    marginTop: 20,
    marginBottom: 8,
    color: '#34495e',
  },
  listContainer: {
    marginVertical: 12,
    marginLeft: 10,
  },
  bulletPoint: {
    color: PRIMARY_COLOR,
    fontWeight: 'bold',
  },
  paragraph: { marginBottom: 12, textAlign: 'justify' },
  heading: {
    fontSize: 18,
    fontWeight: 'bold',
    marginTop: 24,
    marginBottom: 12,
    borderBottom: `1 solid ${BORDER_COLOR}`,
    paddingBottom: 4,
  },
  listItem: { marginBottom: 6, marginLeft: 15, fontSize: DEFAULT_FONT_SIZE },
  table: {
    width: '100%',
    marginVertical: 16,
    borderWidth: 1,
    borderColor: BORDER_COLOR,
  },
  tableRow: { flexDirection: 'row' },
  tableCell: { borderWidth: 1, borderColor: BORDER_COLOR, padding: 8, flex: 1 },
  tableHeaderCell: {
    borderWidth: 1,
    borderColor: TABLE_HEADER_BG,
    padding: 10,
    flex: 1,
    backgroundColor: TABLE_HEADER_BG,
  },
  tableHeaderText: {
    fontSize: DEFAULT_FONT_SIZE,
    fontWeight: 'bold',
    color: '#fff',
    textAlign: 'center',
  },
  checkbox: {
    marginLeft: 2, // Reduced margin to position checkboxes closer to text
    marginRight: 2,
    fontSize: DEFAULT_FONT_SIZE,
  },
  footer: {
    position: 'absolute',
    bottom: 25, // Adjusted for 25mm bottom margin
    left: 25, // Adjusted for 25mm left margin
    right: 25, // Adjusted for 25mm right margin
    textAlign: 'center',
    fontSize: 9,
    color: '#95a5a6',
    borderTop: `1 solid ${BORDER_COLOR}`,
    paddingTop: 10,
  },
});

/* -------------------------------------------------------------------------- */
/*                               HELPER FUNCTIONS                             */
/* -------------------------------------------------------------------------- */

/**
 * Check if a checkbox element is checked
 * @param {HTMLElement} el - Checkbox element
 * @returns {boolean} Whether the checkbox is checked
 */
const isCheckboxChecked = (el) =>
  el.checked ||
  ['on', 'true', 'checked'].includes(el.value?.toLowerCase?.()) ||
  el.hasAttribute('checked');

/**
 * Check if a data value represents a checked state
 * @param {string} val - Value to check
 * @returns {boolean} Whether the value represents checked state
 */
const isDataCheckedTrue = (val) =>
  ['true', 'checked', '1', 'yes'].includes(val?.toLowerCase?.());

/**
 * Check if an element should be skipped during processing
 * @param {HTMLElement} el - Element to check
 * @returns {boolean} Whether the element should be skipped
 */
const shouldSkipElement = (el) => el.matches(UI_ELEMENTS_SELECTOR);

/**
 * Check if an element is a PDF checkbox span
 * @param {HTMLElement} el - Element to check
 * @returns {boolean} Whether the element is a PDF checkbox span
 */
const isPdfCheckboxSpan = (el) =>
  el.tagName === 'SPAN' && el.classList.contains('pdf-checkbox');

/* -------------------------------------------------------------------------- */
/*                              HTML CLEANING LOGIC                           */
/* -------------------------------------------------------------------------- */

/**
 * Clean HTML content by removing UI elements and scripts
 * @param {string|HTMLElement} htmlContent - HTML content to clean
 * @returns {HTMLElement} Cleaned DOM element
 */
export const cleanHtmlContent = (htmlContent) => {
  validateBrowserEnvironment();

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML =
    typeof htmlContent === 'string'
      ? htmlContent
      : htmlContent?.outerHTML || '';

  tempDiv.querySelectorAll(UI_ELEMENTS_SELECTOR).forEach((el) => el.remove());
  tempDiv.querySelectorAll(SCRIPT_STYLE_SELECTOR).forEach((el) => el.remove());

  return tempDiv;
};

/* -------------------------------------------------------------------------- */
/*                           NODE CLASSIFICATION                              */
/* -------------------------------------------------------------------------- */

/**
 * Classify a DOM node based on its type and content
 * @param {Node} node - DOM node to classify
 * @returns {Object} Classification object with type and content
 */
const classifyNode = (node) => {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent.trim();
    return text ? { type: 'text', content: text } : { type: 'skip' };
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return { type: 'skip' };
  if (shouldSkipElement(node)) return { type: 'skip' };

  const tag = node.tagName;

  if (/^H[1-6]$/.test(tag)) {
    return { type: 'heading', level: +tag[1], content: node.textContent.trim() };
  }

  if (tag === 'P') return { type: 'paragraph', content: node.textContent.trim() };
  if (tag === 'INPUT' && node.type === 'checkbox')
    return { type: 'checkbox', checked: isCheckboxChecked(node) };
  if (isPdfCheckboxSpan(node))
    return { type: 'checkbox', checked: isDataCheckedTrue(node.getAttribute('data-checked')) };

  if (tag === 'TEXTAREA')
    return { type: 'textarea', content: (node.value || node.textContent || '').trim() };

  if (tag === 'UL' || tag === 'OL')
    return { type: 'list', ordered: tag === 'OL', items: Array.from(node.children).map((li) => li.textContent.trim()) };

  if (tag === 'TABLE') return { type: 'table', rows: Array.from(node.querySelectorAll('tr')).map((tr) => Array.from(tr.cells).map((td) => td.textContent.trim())) };

  if (tag === 'BR') return { type: 'linebreak' };

  return { type: 'skip' };
};

/* -------------------------------------------------------------------------- */
/*                             EXPORTED FUNCTION                              */
/* -------------------------------------------------------------------------- */

/**
 * Extract structured content from a DOM element
 * @param {HTMLElement} element - DOM element to extract content from
 * @returns {Array} Array of structured content objects
 */
export const extractContent = (element) => {
  validateBrowserEnvironment();
  const elements = [];

  const traverse = (node) => {
    const classification = classifyNode(node);
    if (classification.type !== 'skip') {
      elements.push(classification);
      // Don't traverse children for elements that we've already processed
      if (['heading', 'paragraph', 'checkbox', 'textarea', 'list', 'table'].includes(classification.type)) {
        return;
      }
    }
    // Only traverse children for elements we haven't processed
    node.childNodes.forEach(traverse);
  };

  traverse(element);
  return elements;
};

/* -------------------------------------------------------------------------- */
/*                              HELPER FUNCTIONS                              */
/* -------------------------------------------------------------------------- */

/**
 * Extract structured content from DOM element
 * @param {HTMLElement} element - DOM element to extract content from
 * @returns {Array} Array of structured content objects
 */
const extractStructuredContent = (element) => {
  const elements = [];
  
  const processNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent.trim();
      if (text) {
        elements.push({ type: 'text', content: text });
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      // Skip UI elements
      if (node.tagName === 'BUTTON' || 
          node.classList.contains('MuiButton-root') ||
          node.classList.contains('MuiBox-root') ||
          node.classList.contains('MuiTypography-root')) {
        return;
      }
      
      // Skip header div since we'll add it separately
      if (node.tagName === 'DIV' && node.style && node.style.textAlign === 'center') {
        return;
      }
      
      // Handle different HTML elements
      if (node.tagName === 'H1' || node.tagName === 'H2' || node.tagName === 'H3' || 
          node.tagName === 'H4' || node.tagName === 'H5' || node.tagName === 'H6') {
        const text = node.textContent.trim();
        if (text) {
          elements.push({ 
            type: 'heading', 
            level: parseInt(node.tagName.charAt(1)), 
            content: text 
          });
        }
      } else if (node.tagName === 'P') {
        const text = node.textContent.trim();
        if (text) {
          elements.push({ type: 'paragraph', content: text });
        }
      } else if (node.tagName === 'UL' || node.tagName === 'OL') {
        const listItems = [];
        Array.from(node.children).forEach((li) => {
          const text = li.textContent.trim();
          if (text) {
            listItems.push(text);
          }
        });
        if (listItems.length > 0) {
          elements.push({ 
            type: 'list', 
            ordered: node.tagName === 'OL',
            items: listItems 
          });
        }
      } else if (node.tagName === 'TABLE') {
        const tableData = [];
        Array.from(node.children).forEach((row) => {
          const rowData = [];
          Array.from(row.children).forEach((cell) => {
            const text = cell.textContent.trim();
            rowData.push(text);
          });
          if (rowData.length > 0) {
            tableData.push(rowData);
          }
        });
        if (tableData.length > 0) {
          elements.push({ type: 'table', data: tableData });
        }
      } else if (node.tagName === 'BR') {
        elements.push({ type: 'linebreak' });
      } else if (node.tagName === 'DIV') {
        // Process div content recursively, but skip UI elements
        if (!node.classList.contains('MuiBox-root') && 
            !node.classList.contains('MuiButton-root') &&
            !node.classList.contains('MuiTypography-root')) {
          Array.from(node.childNodes).forEach(processNode);
        }
      } else {
        // For other elements, process their text content
        const text = node.textContent.trim();
        if (text) {
          elements.push({ type: 'text', content: text });
        }
      }
    }
  };
  
  Array.from(element.childNodes).forEach(processNode);
  return elements;
};

/**
 * Render content element based on its type
 * @param {Object} element - Content element to render
 * @param {number} index - Element index
 * @returns {JSX.Element} Rendered element
 */
const renderContentElement = (element, index) => {
  switch (element.type) {
    case 'heading':
      return (
        <Text key={index} style={[
          element.level === 1 ? styles.heading : styles.subheading,
          { fontSize: element.level === 1 ? 18 : element.level === 2 ? 16 : 14 }
        ]}>
          {element.content}
        </Text>
      );
    case 'paragraph':
      return (
        <Text key={index} style={styles.paragraph}>
          {element.content}
        </Text>
      );
    case 'list':
      return (
        <View key={index} style={styles.listContainer}>
          {element.items.map((item, itemIndex) => (
            <View key={itemIndex} style={{ flexDirection: 'row', marginBottom: 4 }}>
              <Text style={styles.bulletPoint}>
                {element.ordered ? `${itemIndex + 1}.` : '•'}
              </Text>
              <Text style={[styles.listItem, { marginLeft: 8 }]}>
                {item}
              </Text>
            </View>
          ))}
        </View>
      );
    case 'table':
      return (
        <View key={index} style={styles.table}>
          {element.data.map((row, rowIndex) => (
            <View key={rowIndex} style={styles.tableRow}>
              {row.map((cell, cellIndex) => (
                <View key={cellIndex} style={[
                  rowIndex === 0 ? styles.tableHeaderCell : styles.tableCell
                ]}>
                  <Text style={{
                    fontSize: rowIndex === 0 ? DEFAULT_FONT_SIZE : 10,
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    lineHeight: 1.3
                  }}>
                    {cell}
                  </Text>
                </View>
              ))}
            </View>
          ))}
        </View>
      );
    case 'checkbox':
      return (
        <Text key={index} style={styles.checkbox}>
          {element.checked ? CHECKED_CHECKBOX : UNCHECKED_CHECKBOX}
        </Text>
      );
    case 'linebreak':
      return <Text key={index}>{"\n"}</Text>;
    default:
      return (
        <Text key={index} style={styles.paragraph}>
          {element.content}
        </Text>
      );
  }
};

/**
 * Generate a simple PDF from HTML content using React PDF
 * @param {string} htmlContent - HTML content to convert to PDF
 * @param {string} templateName - Name of the template
 * @param {string} clientName - Name of the client
 * @param {string} [orientation='portrait'] - Page orientation
 */
export const generateSimplePDF = async (htmlContent, templateName, clientName, orientation = 'portrait') => {
  try {
    console.log('Generating simple PDF...');
    
    // Clean the HTML content
    const cleanedElement = cleanHtmlContent(htmlContent);
    const structuredContent = extractStructuredContent(cleanedElement);
    
    // Create the PDF document
    const MyDocument = () => (
      <Document>
        <Page size="A4" orientation={orientation} style={styles.page}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.title}>{templateName || 'Document'}</Text>
            {clientName && (
              <Text style={styles.subtitle}>Prepared for {clientName}</Text>
            )}
          </View>
          
          {/* Content */}
          <View style={styles.content}>
            {structuredContent.map(renderContentElement)}
          </View>
          
          {/* Footer */}
          <View style={styles.footer}>
            <Text>Fisher Dugmore Financial • Professional Document</Text>
          </View>
        </Page>
      </Document>
    );

    // Generate and download the PDF
    const pdfBytes = await pdf(<MyDocument />).toBlob();
    const url = URL.createObjectURL(pdfBytes);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `${templateName}_${clientName || 'document'}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
    
    console.log('Simple PDF generated successfully');
    
  } catch (error) {
    console.error('Error generating simple PDF:', error);
    throw error;
  }
};

/**
 * Generate PDF with mixed page orientations support
 * @param {string} htmlContent - HTML content with page containers
 * @param {string} templateName - Name of the template
 * @param {string} clientName - Name of the client
 */
export const generateMixedOrientationPDF = async (htmlContent, templateName, clientName) => {
  try {
    console.log('Generating PDF with mixed orientations...');
    
    // Parse the HTML to extract page containers
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    
    const pageContainers = tempDiv.querySelectorAll('.page-container');
    console.log(`Found ${pageContainers.length} page containers`);
    
    if (pageContainers.length === 0) {
      // Fallback to single page
      return await generateSimplePDF(htmlContent, templateName, clientName, 'portrait');
    }
    
    // Create the PDF document with multiple pages
    const MyDocument = () => (
      <Document>
        {Array.from(pageContainers).map((pageContainer, pageIndex) => {
          const orientation = pageContainer.classList.contains('landscape') ? 'landscape' : 'portrait';
          const pageContent = pageContainer.querySelector('.page-content');
          
          if (!pageContent) {
            return (
              <Page key={pageIndex} size="A4" orientation={orientation} style={styles.page}>
                <View style={styles.content}>
                  <Text style={styles.paragraph}>Page {pageIndex + 1}</Text>
                </View>
              </Page>
            );
          }
          
          // Extract content from the page
          const cleanedElement = cleanHtmlContent(pageContent);
          const structuredContent = extractStructuredContent(cleanedElement);
          
          return (
            <Page key={pageIndex} size="A4" orientation={orientation} style={styles.page}>
              {/* Header only on first page */}
              {pageIndex === 0 && (
                <View style={styles.header}>
                  <Text style={styles.title}>{templateName || 'Document'}</Text>
                  {clientName && (
                    <Text style={styles.subtitle}>Prepared for {clientName}</Text>
                  )}
                </View>
              )}
              
              {/* Content with proper formatting */}
              <View style={styles.content}>
                {structuredContent.map(renderContentElement)}
              </View>
              
              {/* Footer */}
              <View style={styles.footer}>
                <Text>Fisher Dugmore Financial • Professional Document</Text>
              </View>
            </Page>
          );
        })}
      </Document>
    );

    // Generate and download the PDF
    const pdfBytes = await pdf(<MyDocument />).toBlob();
    const url = URL.createObjectURL(pdfBytes);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `${templateName}_${clientName || 'document'}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
    
    console.log('PDF generated successfully with mixed orientations');
    
  } catch (error) {
    console.error('Error generating mixed orientation PDF:', error);
    throw error;
  }
};
